import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { getStudentAvatar } from '../../../shared/studentAvatar';

function SelectorAvatar({ student, size = { xs: 126, sm: 150, md: 166 } }) {
  const theme = getStudentAvatar(student?.id, student?.avatar_id, student?.starter_emoji);

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: '#fff',
        border: '4px solid rgba(255,255,255,0.98)',
        boxShadow: 'inset 0 -12px 0 rgba(15,23,42,0.06), 0 8px 18px rgba(88,28,135,0.16)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 9, borderRadius: '50%', bgcolor: theme.bgColor, opacity: 0.68 }} />
      <Box sx={{ position: 'absolute', left: '17%', top: '21%', color: '#facc15', fontSize: { xs: 17, md: 22 }, fontWeight: 1000 }}>✦</Box>
      <Box sx={{ position: 'absolute', right: '18%', top: '28%', color: '#c084fc', fontSize: { xs: 12, md: 17 }, fontWeight: 1000 }}>✦</Box>
      <Box
        sx={{
          position: 'absolute',
          left: '17%',
          right: '17%',
          bottom: 8,
          height: { xs: 20, md: 25 },
          borderRadius: '50% 50% 0 0',
          bgcolor: '#84cc16',
          boxShadow: 'inset 0 6px 0 #bef264',
        }}
      />
      {theme.imageUrl ? <Box component="img" src={theme.imageUrl} alt="" loading="lazy" decoding="async"
        sx={{ position: 'relative', zIndex: 1, width: '86%', height: '86%', objectFit: 'contain' }} /> : <Typography
        component="span"
        sx={{
          position: 'relative',
          zIndex: 1,
          fontSize: { xs: 78, sm: 92, md: 104 },
          lineHeight: 1,
          filter: 'drop-shadow(0 5px 0 rgba(15,23,42,0.12))',
          transform: 'translateY(-2px)',
        }}
      >
        {theme.avatar}
      </Typography>}
    </Box>
  );
}

function SideStudentCard({ student, side, animationStep, running }) {
  const studentName = student?.display_name || 'Öğrenci';
  const theme = getStudentAvatar(student?.id, student?.avatar_id, student?.starter_emoji);
  const isLeft = side === 'left';

  return (
    <Box
      key={`${side}-${student?.id || 'student'}-${animationStep}`}
      sx={{
        display: { xs: 'none', sm: 'flex' },
        position: 'absolute',
        top: { sm: 162, md: 172 },
        left: isLeft ? { sm: 25, md: 42 } : 'auto',
        right: isLeft ? 'auto' : { sm: 25, md: 42 },
        width: { sm: 118, md: 138 },
        height: { sm: 132, md: 150 },
        borderRadius: 3,
        bgcolor: theme.bgColor,
        border: `3px solid ${theme.borderColor}`,
        boxShadow: '0 12px 22px rgba(236,72,153,0.16)',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.7,
        opacity: running ? 0.58 : 0.48,
        filter: 'blur(2px)',
        transform: isLeft ? 'rotate(-7deg) translateX(-8px) scale(0.92)' : 'rotate(7deg) translateX(8px) scale(0.92)',
        overflow: 'hidden',
        animation: running
          ? isLeft
            ? 'selectorCardFlowLeft 240ms ease-out'
            : 'selectorCardFlowRight 240ms ease-out'
          : 'none',
        '@keyframes selectorCardFlowLeft': {
          '0%': { transform: 'translateX(62px) rotate(-2deg) scale(1.03)', opacity: 0.76, filter: 'blur(0.5px)' },
          '100%': { transform: 'translateX(-8px) rotate(-7deg) scale(0.92)', opacity: 0.58, filter: 'blur(2px)' },
        },
        '@keyframes selectorCardFlowRight': {
          '0%': { transform: 'translateX(36px) rotate(10deg) scale(0.88)', opacity: 0.3, filter: 'blur(3px)' },
          '100%': { transform: 'translateX(8px) rotate(7deg) scale(0.92)', opacity: 0.58, filter: 'blur(2px)' },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          width: 92,
          height: 2,
          bgcolor: 'rgba(255,255,255,0.85)',
          boxShadow: '0 -12px 0 rgba(255,255,255,0.55), 0 12px 0 rgba(255,255,255,0.55)',
          left: isLeft ? '100%' : 'auto',
          right: isLeft ? 'auto' : '100%',
        },
      }}
    >
      <SelectorAvatar student={student} size={{ sm: 66, md: 78 }} />
      <Typography sx={{ color: '#4c1d95', fontWeight: 1000, fontSize: { sm: 20, md: 23 }, lineHeight: 1 }}>
        {studentName}
      </Typography>
    </Box>
  );
}

function SelectedBadge({ selectedName }) {
  return (
    <Box
      sx={{
        mt: { xs: 1.3, md: 1.6 },
        mx: 'auto',
        width: 'fit-content',
        minWidth: { xs: 246, sm: 296 },
        borderRadius: 1.2,
        px: { xs: 2.2, md: 3 },
        py: { xs: 0.75, md: 0.9 },
        bgcolor: '#fff7dd',
        border: '2px solid #fed7aa',
        boxShadow: '0 4px 0 rgba(245,158,11,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1, md: 1.2 },
      }}
    >
      <StarRoundedIcon sx={{ color: '#facc15', fontSize: { xs: 31, md: 36 }, filter: 'drop-shadow(0 2px 0 rgba(180,83,9,0.22))' }} />
      <Typography
        sx={{
          color: '#252064',
          fontWeight: 1000,
          fontSize: { xs: 18, sm: 21, md: 23 },
          lineHeight: 1,
          whiteSpace: { xs: 'normal', sm: 'nowrap' },
        }}
      >
        Seçilen öğrenci: <Box component="span" sx={{ color: '#ef3d75' }}>{selectedName}</Box>
      </Typography>
      <StarRoundedIcon sx={{ color: '#facc15', fontSize: { xs: 31, md: 36 }, filter: 'drop-shadow(0 2px 0 rgba(180,83,9,0.22))' }} />
    </Box>
  );
}

function MainStudentCard({ student, running, animationStep }) {
  const studentName = student?.display_name || 'Öğrenci';
  const theme = getStudentAvatar(student?.id, student?.avatar_id, student?.starter_emoji);

  return (
    <Box
      key={`${student?.id || 'student-selector-card'}-${animationStep}`}
      sx={{
        position: 'relative',
        zIndex: 2,
        width: { xs: 238, sm: 268, md: 288 },
        minHeight: { xs: 256, sm: 286, md: 306 },
        borderRadius: 4,
        p: { xs: 1.4, sm: 1.65 },
        bgcolor: theme.bgColor,
        border: `4px solid ${theme.borderColor}`,
        boxShadow: running
          ? '0 10px 22px rgba(236,72,153,0.18), inset 0 0 0 4px rgba(255,255,255,0.55)'
          : '0 12px 0 rgba(236,72,153,0.12), 0 20px 28px rgba(88,28,135,0.15), inset 0 0 0 4px rgba(255,255,255,0.62)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        overflow: 'hidden',
        animation: running ? 'mainSelectorFlow 240ms ease-out' : 'mainSelectorSettle 620ms cubic-bezier(.2,1.35,.35,1)',
        '@keyframes mainSelectorFlow': {
          '0%': { transform: 'translateX(92px) scale(0.91)', opacity: 0.52, filter: 'blur(2px)' },
          '65%': { transform: 'translateX(-5px) scale(1.015)', opacity: 1, filter: 'blur(0)' },
          '100%': { transform: 'translateX(0) scale(1)', opacity: 1, filter: 'blur(0)' },
        },
        '@keyframes mainSelectorSettle': {
          '0%': { transform: 'translateY(9px) scale(0.96)' },
          '70%': { transform: 'translateY(-5px) scale(1.035)' },
          '100%': { transform: 'translateY(0) scale(1)' },
        },
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        <SelectorAvatar student={student} />
      </Box>
      <Typography
        sx={{
          color: '#3b178f',
          fontWeight: 1000,
          fontSize: { xs: 41, sm: 46, md: 50 },
          lineHeight: 1,
          textAlign: 'center',
          overflowWrap: 'anywhere',
          maxWidth: '100%',
          textShadow: '0 3px 0 rgba(255,255,255,0.86)',
        }}
      >
        {studentName}
      </Typography>
    </Box>
  );
}

export default function RandomStudentSelectorModal({
  open,
  previousStudent,
  displayStudent,
  nextStudent,
  selectedStudent,
  running,
  statusText,
  animationStep,
  onSelectAgain,
  onClose,
}) {
  const selectedName = selectedStudent?.display_name || displayStudent?.display_name || 'Öğrenci';
  const accentStars = [
    { top: 58, left: { xs: 30, md: 54 }, size: 43, color: '#facc15', rotate: '-11deg' },
    { top: 94, right: { xs: 44, md: 83 }, size: 26, color: '#c084fc', rotate: '7deg' },
    { bottom: 96, left: { xs: 48, md: 78 }, size: 22, color: '#a855f7', rotate: '-5deg' },
    { bottom: 116, right: { xs: 40, md: 68 }, size: 26, color: '#a855f7', rotate: '13deg' },
    { top: 128, right: { xs: 16, md: 42 }, size: 35, color: '#facc15', rotate: '15deg' },
  ];

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="random-student-selector-title">
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(23,19,39,0.46)',
          backdropFilter: 'blur(1.5px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 1, sm: 2.2 },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 'min(94vw, 610px)',
            maxHeight: '92vh',
            overflowX: 'hidden',
            overflowY: 'auto',
            borderRadius: { xs: 4, md: 5 },
            bgcolor: 'rgba(255,255,255,0.96)',
            border: '4px solid #d8c8ff',
            boxShadow: '0 7px 0 rgba(124,58,237,0.24), 0 28px 52px rgba(23,19,39,0.34), inset 0 0 26px rgba(167,139,250,0.16)',
            px: { xs: 1.5, sm: 2.5, md: 3 },
            pt: { xs: 2.4, md: 2.7 },
            pb: { xs: 2, md: 2.4 },
            textAlign: 'center',
            backgroundImage:
              'radial-gradient(circle at 18% 20%, rgba(236,72,153,0.08) 0 52px, transparent 53px), radial-gradient(circle at 84% 23%, rgba(167,139,250,0.14) 0 62px, transparent 63px), linear-gradient(180deg, rgba(255,255,255,0.98), rgba(250,245,255,0.96))',
          }}
        >
          <IconButton
            aria-label="Kapat"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: { xs: 11, md: 14 },
              right: { xs: 11, md: 14 },
              zIndex: 4,
              width: { xs: 43, md: 49 },
              height: { xs: 43, md: 49 },
              bgcolor: '#b285ff',
              color: '#fff',
              border: '3px solid rgba(255,255,255,0.88)',
              boxShadow: '0 4px 0 rgba(91,33,182,0.24)',
              '&:hover': { bgcolor: '#9f6df5' },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
          </IconButton>

          {accentStars.map((star) => (
            <StarRoundedIcon
              key={`${star.top || star.bottom}-${star.left || star.right}`}
              sx={{
                position: 'absolute',
                top: star.top,
                bottom: star.bottom,
                left: star.left,
                right: star.right,
                color: star.color,
                fontSize: { xs: Math.max(18, star.size - 8), md: star.size },
                transform: `rotate(${star.rotate})`,
                filter: star.color === '#facc15' ? 'drop-shadow(0 3px 0 rgba(180,83,9,0.22))' : 'none',
                opacity: 0.95,
              }}
            />
          ))}

          <Typography
            id="random-student-selector-title"
            sx={{
              color: '#4c1d95',
              fontWeight: 1000,
              fontSize: { xs: 34, sm: 42, md: 46 },
              lineHeight: 1,
              pr: { xs: 4.4, md: 5 },
              textShadow: '0 3px 0 rgba(255,255,255,0.95)',
            }}
          >
            Kim Seçilecek?
          </Typography>

          <Typography
            sx={{
              mt: { xs: 0.6, md: 0.75 },
              color: running ? '#5b21b6' : '#4c1d95',
              fontWeight: 900,
              fontSize: { xs: 20, sm: 24, md: 26 },
              minHeight: { xs: 28, md: 32 },
            }}
          >
            {statusText}
          </Typography>

          <Box
            sx={{
              position: 'relative',
              mt: { xs: 1.1, md: 1.35 },
              minHeight: { xs: 264, sm: 298, md: 316 },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              perspective: 900,
            }}
          >
            <SideStudentCard student={previousStudent} side="left" animationStep={animationStep} running={running} />
            <MainStudentCard student={displayStudent} running={running} animationStep={animationStep} />
            <SideStudentCard student={nextStudent} side="right" animationStep={animationStep} running={running} />
          </Box>

          {!running && selectedStudent && <SelectedBadge selectedName={selectedName} />}

          {!running && selectedStudent && (
            <Box
              sx={{
                mt: { xs: 1.7, md: 2 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 1.15, sm: 2.2 },
              }}
            >
              <Button
                variant="outlined"
                onClick={onSelectAgain}
                startIcon={<LoopRoundedIcon />}
                sx={{
                  width: { xs: '100%', sm: 210 },
                  minHeight: { xs: 52, md: 56 },
                  borderRadius: 99,
                  borderWidth: 2,
                  borderColor: '#7c3aed',
                  color: '#5b21b6',
                  bgcolor: '#fff',
                  fontWeight: 1000,
                  fontSize: { xs: 17, md: 20 },
                  textTransform: 'none',
                  '& .MuiButton-startIcon': { mr: 1 },
                  '&:hover': { borderWidth: 2, borderColor: '#6d28d9', bgcolor: '#faf5ff' },
                }}
              >
                Tekrar Seç
              </Button>
              <Button
                variant="contained"
                onClick={onClose}
                startIcon={<CheckRoundedIcon />}
                sx={{
                  width: { xs: '100%', sm: 210 },
                  minHeight: { xs: 52, md: 56 },
                  borderRadius: 99,
                  bgcolor: '#7c3aed',
                  color: '#fff',
                  fontWeight: 1000,
                  fontSize: { xs: 17, md: 20 },
                  textTransform: 'none',
                  boxShadow: '0 5px 0 rgba(76,29,149,0.30)',
                  '& .MuiButton-startIcon': { mr: 1 },
                  '&:hover': { bgcolor: '#6d28d9' },
                }}
              >
                Tamam
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
